import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import wizardStyles from "../styles/AddGifteeWizard.module.css";
import styles from "./GifteeCustomOccasionSheet.module.css";
import {
  formatShortCelebrationDateInput,
  isValidCalendarMMDDYY,
  timestampToMmDdYy,
} from "../utils/celebrationDateInput.js";

export default function GifteeCustomOccasionSheet({
  open,
  editReminder,
  onClose,
  onSave,
  onDeleteReminder,
}) {
  const [name, setName] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [annual, setAnnual] = useState(false);

  const isEdit =
    Boolean(editReminder?.id) && editReminder?.type === "custom";

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /** Hydrate when opening or switching which reminder we edit (`id`). Not reminder ref identity. */
  useEffect(() => {
    if (!open) return;
    const r = editReminder;
    if (r && r.type === "custom") {
      setName((r.title || "").trim());
      setDateStr(timestampToMmDdYy(r.date));
      setAnnual(r.repeat !== "once");
    } else {
      setName("");
      setDateStr("");
      setAnnual(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate by open + reminder id only (avoid snapshot churn)
  }, [open, editReminder?.id]);

  if (!open) return null;

  const trimmed = name.trim();
  const canSave = Boolean(trimmed) && isValidCalendarMMDDYY(dateStr);

  const handleLeftPrimary = () => {
    if (isEdit && typeof onDeleteReminder === "function") {
      onDeleteReminder();
      return;
    }
    onClose();
  };

  const handleSaveClick = () => {
    if (!canSave) return;
    onSave({ title: trimmed, mmddyy: dateStr, annual });
  };

  return createPortal(
    <div className={styles.root}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label="Dismiss"
        onClick={onClose}
      />
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-custom-occasion-title"
      >
        <button
          type="button"
          className={wizardStyles.customOccCloseBtn}
          onClick={onClose}
          aria-label="Close custom celebration"
        >
          <i className="fa-solid fa-xmark" aria-hidden />
        </button>
        <div className={wizardStyles.customOccDrawerBody}>
          <p
            id="profile-custom-occasion-title"
            className={wizardStyles.customOccTitle}
          >
            Custom Celebration
          </p>
          <input
            type="text"
            className={wizardStyles.customOccNameInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Title"
            autoFocus
          />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className={wizardStyles.customOccDateInput}
            value={dateStr}
            onChange={(e) =>
              setDateStr(formatShortCelebrationDateInput(e.target.value))
            }
            placeholder="MM/DD/YY"
            aria-label="Custom celebration date"
          />
        </div>
        <div className={wizardStyles.customOccDrawerControls}>
          <div className={wizardStyles.toggleRow}>
            <span className={wizardStyles.toggleLabel}>Annual reminder</span>
            <label className={wizardStyles.toggle}>
              <input
                type="checkbox"
                checked={annual}
                onChange={(e) => setAnnual(e.target.checked)}
              />
              <span className={wizardStyles.toggleTrack}>
                <span className={wizardStyles.toggleText}>
                  {annual ? "Yes" : "No"}
                </span>
              </span>
            </label>
          </div>
          <div className={wizardStyles.customOccDrawerButtonRow}>
            <button
              type="button"
              className={wizardStyles.customOccDismissBtn}
              onClick={handleLeftPrimary}
              aria-label={isEdit ? "Delete celebration" : "Discard draft"}
            >
              Delete
            </button>
            <button
              type="button"
              className={`${wizardStyles.customOccSaveBtn} ${wizardStyles.customOccSaveBtnRowFit} ${
                !canSave ? wizardStyles.customOccSaveBtnDisabled : ""
              }`}
              disabled={!canSave}
              onClick={handleSaveClick}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
